import { Injectable, Injector } from '@angular/core';
import { LoggerService } from 'src/app/core/logger.service';
import { NotificationsService } from 'src/app/core/notifications.service';
import { BroadcastService } from 'src/app/shared/services/broadcast.service';
import { IUploadFolderView, IFile, ITransferItemUpload, IDirItem } from 'src/sync';
import { FileUploader } from '../../file-uploader';
import { BuildTransferItemService } from '../queue/build-transfer-item.service';
import { TransferItemService } from '../queue/transfer-item.service';
import { TRANSFER_STATUS } from '../../models/transfer.model';
import { Entry } from 'src/app/filewriter';
import { ExternalNavigationHandlerService } from '../queue/external-navigation-handler.service';
import {
  BUNDLE_FILE_THRESHOLD,
  BUNDLE_MAX_FILES,
  BUNDLE_MAX_SIZE,
  MAX_CP_API_CONCURRENT,
} from '../../transfer.constants';
import { IBundleItem } from '../../models';
import { AsyncSemaphore } from 'src/app/shared/helpers/async-semaphore';

interface IWorkItem {
  pid?: number;
  fullPath: string;
  pidPath: string;
  file: File;
}
interface IDirMappingList {
  [fullPath: string]: IDirItem;
}

const DEFAULT_UPLOAD_FOLDER_VIEW: Readonly<IUploadFolderView> = Object.freeze({
  fileCount: 0,
  filePendingCount: 0,
  cwdName: '',
  currentFolder: '',
  folderCount: 0,
  folderMadeCount: 0,
  totalBytes: 0,
  pendingBytes: 0,
  fileErrorCount: 0,
  current: undefined,
  isProcessing: false,
  isMakingFolders: false,
  isMakingTransfers: false,
  isScanning: false,
  eta: 0,
  error: undefined,
  rootItems: [],
});

@Injectable({
  providedIn: 'root',
})
export class RunUploadFolderService {
  public view: IUploadFolderView = { ...DEFAULT_UPLOAD_FOLDER_VIEW, rootItems: [] };

  public workDirs: IWorkItem[] = [];
  public queueFiles: IWorkItem[] = [];
  public shouldCancel = false;
  public cwd: IFile;
  public dirMap: IDirMappingList = {};
  public workFiles: ITransferItemUpload[] = [];
  public folderUploadInProgress = false;

  // Track number of currently active uploads
  private activeUploadCount = 0;

  // Maximum concurrent uploads (depend on web / mobile browser)
  private maxActiveUploads: number;

  // Guard to prevent concurrent processQueue() execution
  private isProcessingQueue = false;

  // Global semaphore for CP API requests (preUpload + finishUpload)
  // This prevents overwhelming the server when multiple batches run in parallel
  private cpApiSemaphore = new AsyncSemaphore(MAX_CP_API_CONCURRENT);

  // Promise resolver for when all uploads complete
  private queueCompleteResolve: ((value: boolean) => void) | null = null;

  constructor(
    private transferItemService: TransferItemService,
    private loggerService: LoggerService,
    private buildTransferItemService: BuildTransferItemService,
    private notificationsService: NotificationsService,
    private broadcastService: BroadcastService,
    private injector: Injector,
    private externalNavigationHandlerService: ExternalNavigationHandlerService,
  ) {
    this.externalNavigationHandlerService.addHandler(this.navigationHandler);
    this.maxActiveUploads = this.buildTransferItemService.getMaxActiveUploads();
  }

  public restart(): void {
    const result: ITransferItemUpload[] = [];
    let bytes = 0;
    for (let i = 0, len = this.workFiles.length; i < len; i++) {
      const tItem = this.workFiles[i];
      if (tItem.status !== TRANSFER_STATUS.STATUS_SUCCESS) {
        tItem.status = TRANSFER_STATUS.STATUS_WAITING;
        tItem.chunkqueue = this.transferItemService.getChunkQueue(tItem);
        bytes += tItem.filesize;
        result.push(tItem);
      }
    }
    this.reset();
    this.workFiles = result;
    this.view.fileCount = result.length;
    this.view.totalBytes = bytes;
    this.view.error = undefined;
    this.runUploadQueue();
  }

  public runUploadQueue(): Promise<boolean> {
    return new Promise((resolve) => {
      this.queueCompleteResolve = resolve;
      this.processQueue();
    });
  }

  private processQueue(): void {
    // Prevent concurrent processQueue execution
    if (this.isProcessingQueue) {
      return;
    }
    this.isProcessingQueue = true;

    try {
      if (this.shouldCancel) {
        this.loggerService.error('Cancel requested');
        this.completeQueue(false);
        return;
      }

      if (this.view.error) {
        this.view.isProcessing = false;
        this.completeQueue(false);
        return;
      }

      if (!this.workFiles.length) {
        this.view.isProcessing = false;
        this.notificationsService.startNotificationLoop();
        this.completeQueue(false);
        return;
      }

      const next = this.buildTransferItemService.getLatestItem(this.workFiles);
      this.view.filePendingCount = next.pending;
      this.view.pendingBytes = next.pendingBytes;

      // Start uploads up to maxActiveUploads (batches OR individual large files run in PARALLEL)
      while (this.activeUploadCount < this.maxActiveUploads) {
        const waitingIdx = this.findNextWaitingItem();
        if (waitingIdx === -1) {
          break;
        }

        const tItem = this.workFiles[waitingIdx];

        // Large file: upload individually (uses chunked upload via uploadItem)
        if (tItem.filesize >= BUNDLE_FILE_THRESHOLD) {
          tItem.status = TRANSFER_STATUS.STATUS_WORKING;
          this.startUpload(tItem);
          continue;
        }

        // Small file: collect a batch and start it in parallel
        const batch = this.collectBatch();
        if (batch.length > 0) {
          this.startBatch(batch); // Runs in parallel with other batches/uploads
        } else {
          break;
        }
      }

      // Update pending count
      const updated = this.buildTransferItemService.getLatestItem(this.workFiles);
      this.view.filePendingCount = updated.pending;
      this.view.pendingBytes = updated.pendingBytes;

      // Check if all uploads are complete
      if (this.activeUploadCount === 0 && this.findNextWaitingItem() === -1) {
        this.loggerService.info('Completed upload: ' + this.view.fileCount);
        this.notificationsService.startNotificationLoop();
        this.broadcastService.broadcast('event:link-file-list.reload');
        this.view.isProcessing = false;
        this.completeQueue(true);
      }
    } finally {
      this.isProcessingQueue = false;
    }
  }

  /**
   * Collects small files into a batch for bundled upload.
   * Files are marked as WORKING to prevent being picked up again.
   */
  private collectBatch(): ITransferItemUpload[] {
    const batch: ITransferItemUpload[] = [];
    let batchSize = 0;

    while (batch.length < BUNDLE_MAX_FILES && batchSize < BUNDLE_MAX_SIZE) {
      const waitingIdx = this.findNextWaitingItem();
      if (waitingIdx === -1) break;

      const tItem = this.workFiles[waitingIdx];

      // Stop if we hit a large file (will be handled separately)
      if (tItem.filesize >= BUNDLE_FILE_THRESHOLD) break;

      tItem.status = TRANSFER_STATUS.STATUS_WORKING;
      batch.push(tItem);
      batchSize += tItem.filesize;
    }

    return batch;
  }

  /**
   * Starts a batch upload (fire and forget - runs in parallel with other batches).
   * Each batch counts as ONE activeUploadCount slot.
   */
  private startBatch(batch: ITransferItemUpload[]): void {
    this.view.isProcessing = true;
    this.activeUploadCount++;

    // Mark root dirs as uploading
    for (const tItem of batch) {
      const rootPath = tItem.fullPath.split('/').slice(0, 2).join('/');
      if (this.dirMap[rootPath]) {
        this.dirMap[rootPath].status = TRANSFER_STATUS.STATUS_UPLOAD;
      }
    }

    const fileUploader = new FileUploader(this.injector);

    // Execute batch asynchronously
    this.executeBatch(batch, fileUploader)
      .catch((err) => {
        const reason = err instanceof Error ? err.message : String(err);
        this.loggerService.warn(`Batch failed, resetting items for individual upload: ${reason}`);
        for (const tItem of batch) {
          if (tItem.status !== TRANSFER_STATUS.STATUS_SUCCESS) {
            tItem.status = TRANSFER_STATUS.STATUS_WAITING;
          }
        }
      })
      .finally(() => {
        this.activeUploadCount--;
        this.processQueue();
      });
  }

  /**
   * Executes a batch: preUpload → encrypt → bundled upload → finishUpload
   * Global CP API semaphore ensures max concurrent API requests across ALL batches.
   */
  private async executeBatch(batch: ITransferItemUpload[], fileUploader: FileUploader): Promise<void> {
    // PreUpload all (global semaphore throttles to MAX_CP_API_CONCURRENT)
    await Promise.all(
      batch.map(async (t) => {
        await this.cpApiSemaphore.acquire();
        try {
          await fileUploader.preUpload(t);
        } finally {
          this.cpApiSemaphore.release();
        }
      }),
    );

    // Prepare all files for bundling (encryption, chunking)
    const prepared: IBundleItem[] = await Promise.all(batch.map((t) => fileUploader.prepareFileForBundle(t)));

    // Bundled upload (single request)
    const results = await fileUploader.uploadBundledChunks(prepared);

    results.forEach((r, i) => {
      batch[i].blob_id = r.blob_id;
      batch[i].cachekey = r.cachekey;
    });

    const settled = await Promise.allSettled(batch.map((t) => this.finishUploadAndMarkSuccess(t, fileUploader)));
    const failed = settled.filter((r) => r.status === 'rejected');
    if (failed.length > 0) {
      throw new Error(
        `finishUpload failed for ${failed.length}/${batch.length} item(s) in batch: ${this.summarizeFailureReasons(failed as PromiseRejectedResult[])}`,
      );
    }
  }

  /**
   * De-duplicates rejection messages and caps to the first 3 distinct reasons so the
   * error string stays readable when many items in a large batch fail with the same root cause.
   */
  private summarizeFailureReasons(failed: PromiseRejectedResult[]): string {
    const MAX_REASONS = 3;
    const seen = new Set<string>();
    for (const r of failed) {
      seen.add(r.reason instanceof Error ? r.reason.message : String(r.reason));
      if (seen.size >= MAX_REASONS) break;
    }
    const reasons = Array.from(seen).join('; ');
    return failed.length > seen.size ? `${reasons} (+${failed.length - seen.size} more)` : reasons;
  }

  /**
   * Performs finishUpload and immediately marks item as SUCCESS for real-time counter updates.
   */
  private async finishUploadAndMarkSuccess(tItem: ITransferItemUpload, fileUploader: FileUploader): Promise<void> {
    await this.cpApiSemaphore.acquire();
    try {
      if (tItem.linkID) {
        await fileUploader.finishUploadPublic(tItem);
      } else {
        await fileUploader.finishUpload(tItem);
      }
      tItem.status = TRANSFER_STATUS.STATUS_SUCCESS;
      tItem.progress_percent = 100;
      this.handleItemSuccess(tItem);
    } finally {
      this.cpApiSemaphore.release();
    }
  }

  private findNextWaitingItem(): number {
    for (let i = 0; i < this.workFiles.length; i++) {
      if (this.workFiles[i].status === TRANSFER_STATUS.STATUS_WAITING) {
        return i;
      }
    }
    return -1;
  }

  private startUpload(tItem: ITransferItemUpload): void {
    this.view.isProcessing = true;
    this.activeUploadCount++;

    const rootPath = tItem.fullPath.split('/').slice(0, 2).join('/');
    if (this.dirMap[rootPath]) {
      this.dirMap[rootPath].status = TRANSFER_STATUS.STATUS_UPLOAD;
    }

    this.view.current = tItem;

    // Create a new FileUploader instance for each concurrent upload
    const fileUploader = new FileUploader(this.injector);

    fileUploader
      .uploadItem(tItem)
      .then(() => {
        this.activeUploadCount--;
        this.handleItemSuccess(tItem);
        // Process more items from the queue
        this.processQueue();
      })
      .catch(() => {
        this.activeUploadCount--;
        this.handleItemError(tItem);
        this.loggerService.error('Uploading failed for ' + tItem.backup_id);
        // Continue processing despite error
        this.processQueue();
      });
  }

  private completeQueue(success: boolean): void {
    if (this.queueCompleteResolve) {
      this.queueCompleteResolve(success);
      this.queueCompleteResolve = null;
    }
  }

  public reset(): void {
    Object.assign(this.view, DEFAULT_UPLOAD_FOLDER_VIEW, { rootItems: [] });

    this.dirMap = {};
    this.workFiles = [];
    this.workDirs = [];
    this.queueFiles = [];
    this.shouldCancel = false;
    this.cwd = undefined;
    this.folderUploadInProgress = false;
    this.activeUploadCount = 0;
    this.queueCompleteResolve = null;
    this.isProcessingQueue = false;
    this.cpApiSemaphore.reset();
  }

  public cancel(): void {
    this.shouldCancel = true;
    this.reset();
  }

  public markWaitingRootItemsErrored(code?: number): void {
    if (code === undefined) {
      return;
    }
    for (const rootItem of this.view.rootItems) {
      if (rootItem.status === TRANSFER_STATUS.STATUS_WAITING) {
        rootItem.status = code;
      }
    }
  }

  public handleItemSuccess(item: Entry | IDirItem | ITransferItemUpload): void {
    //checking if current item is a subfolder
    const rootPath = item.fullPath.split('/').slice(0, 2).join('/');
    const rootDir = this.dirMap[rootPath];
    if (item.fullPath.split('/').length > 2) {
      rootDir.completedFiles += 1;
    }
    this.finalizeRootDir(rootDir);
  }

  public handleItemError(item: Entry | IDirItem | ITransferItemUpload): void {
    //checking if current item is a subfolder
    const rootPath = item.fullPath.split('/').slice(0, 2).join('/');
    const rootDir = this.dirMap[rootPath];

    if (item.fullPath.split('/').length > 2) {
      rootDir.errorFiles += 1;
      rootDir.errorFilePaths.push(item.fullPath);

      // track error status only if it's not generic
      rootDir.errorStatuses = rootDir.errorStatuses || [];
      if ('status' in item && item.status !== TRANSFER_STATUS.STATUS_ERROR) {
        rootDir.errorStatuses.push(item.status);
      }

      this.view.fileErrorCount += 1;
      this.loggerService.error('Upload error on item ' + item.fullPath);
    }
    this.finalizeRootDir(rootDir);
  }

  private finalizeRootDir(rootDir: IDirItem): void {
    if (rootDir.completedFiles + rootDir.errorFiles === rootDir.totalFiles) {
      if (rootDir.errorFiles > 0) {
        // pick first error != generic(3000) if exists, else generic
        const specificError = rootDir.errorStatuses?.find((code) => code !== TRANSFER_STATUS.STATUS_ERROR);
        rootDir.status = specificError || TRANSFER_STATUS.STATUS_ERROR;
      } else {
        rootDir.status = TRANSFER_STATUS.STATUS_SUCCESS;
      }
    }
  }

  private navigationHandler: () => void = () => {
    if (this.view.isScanning || this.view.isProcessing) {
      return 'There is unfinished upload/download pending.';
    }
    return undefined;
  };
}
